import React, { useRef, useEffect, useState } from 'react';
import styled from 'styled-components';
import {
  brandColours,
  fontSize,
  fontWeights,
  maxBreakpointQuery,
  minBreakpointQuery,
  standardColours,
  standardTransition,
} from '../styles';
import { Button, Svg } from './ui';
import arrow from '../images/arrow-right.inline.svg';

const StyledArrowIcon = styled(Svg)`
  fill: ${standardColours.white};
  transform: rotate(-90deg);
  width: 18px;
  height: 18px;

  ${minBreakpointQuery.small`
    width: 24px;
    height: 24px;
  `}
`;

const StyledFeaturedResourcePopUp = styled.article`
  width: 280px;
  position: fixed;
  bottom: 0;
  right: 50%;
  z-index: 1;
  transform: translateY(${props => props.contentHeight}px);
  transition: ${standardTransition('transform')};

  ${maxBreakpointQuery.small`
    transform: translateX(50%);
  `}

  ${minBreakpointQuery.small`
    right: 60px;
    width: 320px;
  `}

  &:hover {
    transform: translateY(0);

    ${maxBreakpointQuery.small`
      transform: translateX(50%) translateY(0);
    `}
  }

  /* rotate arrow on hover */
  &:hover ${StyledArrowIcon} {
    transform: rotate(90deg);
  }
`;

const StyledInner = styled.div`
  color: ${standardColours.white};
`;

const StyledHeader = styled.header`
  display: flex;
  background-color: ${brandColours.denary};
  padding: 10px 15px;
  gap: 10px;
  align-items: center;
  justify-content: space-between;

  ${minBreakpointQuery.small`
    gap: 20px;
    padding: 15px 20px;
  `}
`;

const StyledHeading = styled.h2`
  ${fontSize(14)};
  font-weight: ${fontWeights.semibold};

  ${minBreakpointQuery.small`
    ${fontSize(16)};
  `}
`;

const StyledContent = styled.div`
  background-color: ${brandColours.octonary};
  padding: 15px;

  ${minBreakpointQuery.small`
    padding: 20px;
  `}
`;

const StyledText = styled.p`
  margin-bottom: 10px;
  ${fontSize(12)};

  ${minBreakpointQuery.small`
    ${fontSize(14)};
  `}

  ${minBreakpointQuery.smedium`
    margin-bottom: 15px;
  `}
`;

const StyledButton = styled(Button)`
  color: ${brandColours.secondary};
  border-radius: 45px;
  width: 100%;
  ${fontSize(12)};

  ${minBreakpointQuery.large`
    ${fontSize(14)};
  `};
`;

const FeaturedResourcePopUp = ({
  ctaHeading,
  ctaText,
  link,
  ctaButtonText,
}) => {
  const [contentHeight, setContentHeight] = useState(0);
  const contentRef = useRef(null);

  const updateContentHeight = () => {
    if (contentRef.current) {
      setContentHeight(contentRef.current.offsetHeight);
    }
  };

  useEffect(() => {
    updateContentHeight();

    window.addEventListener('resize', updateContentHeight);

    return () => {
      window.removeEventListener('resize', updateContentHeight);
    };
  }, []);

  return (
    <StyledFeaturedResourcePopUp contentHeight={contentHeight}>
      <StyledInner>
        <StyledHeader>
          <StyledHeading>{ctaHeading}</StyledHeading>
          <StyledArrowIcon image={arrow} />
        </StyledHeader>
        <StyledContent ref={contentRef}>
          <StyledText>{ctaText}</StyledText>
          <StyledButton to={link.pageUrl}>
            {ctaButtonText ? ctaButtonText : 'Download now'}
          </StyledButton>
        </StyledContent>
      </StyledInner>
    </StyledFeaturedResourcePopUp>
  );
};

export default FeaturedResourcePopUp;
